import { getStore, setStore, subscribe } from '../state/store.js';
import { saveOverride, removeOverride } from '../state/persistence.js';
import { assemblePrompt } from '../data/prompts.js';
import type { Prompt, PromptCatalog } from '../types.js';

class CCPWPromptCard extends HTMLElement {
  override prompt!: Prompt;
  private open = false;
  private unsub?: () => void;
  private copied = false;
  private copyTimer?: number;

  static get observedAttributes() { return ['prompt']; }

  set promptData(p: Prompt) { this.prompt = p; this.render(); }
  get promptData(): Prompt { return this.prompt; }

  connectedCallback() {
    if (!this.shadowRoot) this.attachShadow({ mode: 'open' });
    if (this.prompt) this.render();
    document.addEventListener('ccpw:overrides-reset', this.handleOverridesReset);
  }

  attributeChangedCallback() { /* no-op, use property */ }

  disconnectedCallback() {
    this.unsub?.();
    if (this.copyTimer) clearTimeout(this.copyTimer);
    document.removeEventListener('ccpw:overrides-reset', this.handleOverridesReset);
  }

  // 单独监听"重置"事件而不是订阅整个 store:store 里每次敲键盘(onSlotChange)
  // 也会触发,如果重置也走通用 subscribe 会导致输入时卡片被强制重渲染、丢失焦点。
  private handleOverridesReset = () => this.render();

  private toggle() {
    this.open = !this.open;
    this.render();
  }

  private async copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    this.copied = true;
    this.render();
    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.copyTimer = window.setTimeout(() => { this.copied = false; this.render(); }, 1600);
  }

  private onSlotChange(key: string, val: string) {
    const ov = getStore().overrides[this.prompt.id] ?? {};
    const slots = { ...(ov.slots ?? this.prompt.slots ?? {}), [key]: val };
    const next = { ...ov, slots };
    saveOverride(this.prompt.id, next);
    setStore({ overrides: { ...getStore().overrides, [this.prompt.id]: next } });
  }

  private restore() {
    removeOverride(this.prompt.id);
    const overrides = { ...getStore().overrides };
    delete overrides[this.prompt.id];
    setStore({ overrides });
    document.dispatchEvent(new CustomEvent('ccpw:overrides-reset'));
  }

  private render() {
    if (!this.prompt) return;
    const s = getStore();
    const catalog = (window as any).__ccpwCatalog as PromptCatalog | undefined;
    const ov = s.overrides[this.prompt.id];
    const slots = { ...(this.prompt.slots ?? {}), ...(ov?.slots ?? {}) };
    const srcLabel = catalog?.taxonomy.sourceLabels[this.prompt.src] ?? this.prompt.src;

    const shadow = this.shadowRoot!;
    shadow.innerHTML = `
      <style>
        :host { display: block; min-width: 0; }
        .card {
          border: 1px solid var(--ccpw-border-subtle);
          border-radius: 12px;
          background: var(--ccpw-bg); overflow: hidden;
          padding: 18px 22px; transition: border-color 200ms;
          font-family: var(--ccpw-font-sans);
          min-width: 0; overflow-wrap: break-word; word-break: break-word;
        }
        .card.open { border-color: var(--ccpw-border); background: var(--ccpw-surface); }
        .head {
          display: flex; align-items: baseline; gap: 12px;
          padding: 6px 8px; margin: -6px -8px;
          border-radius: 8px; transition: background-color 150ms;
        }
        .head:hover { background: var(--ccpw-accent-bg); }
        .title { flex: 1; font-size: 17.5px; font-weight: 500; color: var(--ccpw-text); cursor: pointer; transition: color 150ms; }
        .head:hover .title { color: var(--ccpw-accent); }
        .chip { font-size: 11px; padding: 3px 9px; border-radius: 999px; background: var(--ccpw-accent-bg); color: var(--ccpw-accent); font-family: var(--ccpw-mono); }
        .preview { display: block; font-family: var(--ccpw-mono); font-size: 13.5px; color: var(--ccpw-text-3); margin-top: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .body { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--ccpw-border-subtle); }
        .label { font-size: 11.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ccpw-text-4); margin: 12px 0 8px; font-family: var(--ccpw-mono); }
        .prompt-box { display: flex; align-items: center; gap: 10px; padding: 14px 16px; background: #0a0e14; color: #f0eee6; border-radius: 8px; font-family: var(--ccpw-mono); font-size: 15px; flex-wrap: wrap; min-width: 0; }
        .prompt-box > span { min-width: 0; }
        .caret { color: var(--ccpw-accent); flex-shrink: 0; }
        .slot { background: rgba(132,204,22,0.15); color: #f0eee6; border: none; border-bottom: 1.5px dashed var(--ccpw-accent); border-radius: 4px 4px 0 0; padding: 2px 6px; margin: 0 1px; outline: none; min-width: 8ch; max-width: 100%; font-family: inherit; transition: background-color 150ms; }
        .slot:hover { background: rgba(132,204,22,0.24); }
        .copy { font-size: 12.5px; padding: 6px 12px; border-radius: 6px; background: var(--ccpw-accent); color: #0a0e14; border: none; font-weight: 500; transition: filter 150ms; }
        .copy:hover { filter: brightness(1.08); }
        .reset { font-size: 12.5px; padding: 6px 12px; border-radius: 6px; background: none; border: 1px solid rgba(240,238,230,0.25); color: #f0eee6; font-weight: 500; margin-left: auto; transition: background-color 150ms, border-color 150ms; }
        .reset:hover { background: var(--ccpw-accent-bg); border-color: var(--ccpw-accent); }
        .teaches { font-size: 15.5px; color: var(--ccpw-text-2); line-height: 1.6; margin-top: 4px; }
        .next { display: flex; align-items: baseline; gap: 10px; margin: 14px 0 0; padding: 10px 12px; background: var(--ccpw-accent-bg); border-radius: 8px; font-size: 14.5px; }
        .next-label { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--ccpw-accent); font-weight: 600; flex-shrink: 0; font-family: var(--ccpw-mono); }
        .src { font-size: 13px; color: var(--ccpw-text-4); margin-top: 12px; }
      </style>
      <div class="card ${this.open ? 'open' : ''} ccpw-card">
        <button type="button" class="head" style="all:unset; display:flex; align-items:baseline; gap:12px; cursor:pointer; width:100%;">
          <span class="title">${escapeHtml(this.prompt.title)}</span>
          ${this.prompt.startN ? `<span class="chip">新手入门 · ${this.prompt.startN}</span>` : ''}
        </button>
        <code class="preview">${escapeHtml(this.previewPrompt())}</code>
        ${this.open ? `
          <div class="body">
            <div class="label">${this.prompt.slots ? '填写并复制' : '复制这条提示词'}</div>
            <div class="prompt-box">
              <span class="caret">❯</span>
              ${this.renderPromptBody(slots)}
              <button type="button" class="reset">重置</button>
              <button type="button" class="copy">${this.copied ? '已复制' : '复制'}</button>
            </div>
            <div class="label">为什么有效</div>
            <div class="teaches">${escapeHtml(this.prompt.teaches)}</div>
            ${this.prompt.next ? `
              <div class="next">
                <span class="next-label">巩固记忆</span>
                <span>${escapeHtml(this.prompt.next)}</span>
              </div>
            ` : ''}
            <div class="src">来源:${escapeHtml(srcLabel)}</div>
          </div>
        ` : ''}
      </div>
    `;

    const headBtn = shadow.querySelector('.head') as HTMLButtonElement;
    headBtn?.addEventListener('click', () => this.toggle());
    const copyBtn = shadow.querySelector('.copy') as HTMLButtonElement | null;
    copyBtn?.addEventListener('click', () => this.copy(this.previewPrompt()));
    shadow.querySelectorAll<HTMLInputElement>('.slot').forEach(input => {
      input.addEventListener('input', () => {
        // 输入框宽度跟着内容动态调整,避免长文本被固定宽度截断/隐藏
        input.size = Math.max(input.value.length, 6);
        this.onSlotChange(input.dataset.key!, input.value);
      });
      // blur 时才重渲染(而不是每次按键都渲染),这样"重置"按钮会在用户
      // 填完离开输入框后出现,同时不会在打字过程中打断输入焦点。
      input.addEventListener('blur', () => this.render());
    });
    const resetBtn = shadow.querySelector('.reset') as HTMLButtonElement | null;
    resetBtn?.addEventListener('click', () => this.restore());
  }

  private previewPrompt(): string {
    return assemblePrompt(this.prompt, getStore().overrides[this.prompt.id]);
  }

  private renderPromptBody(slots: Record<string, string>): string {
    const tmpl = this.prompt.prompt;
    return tmpl.split(/(\{\w+\})/g).map(part => {
      const m = part.match(/^\{(\w+)\}$/);
      if (!m) return `<span>${escapeHtml(part)}</span>`;
      const k = m[1]!;
      const val = slots[k] ?? '';
      const placeholder = this.prompt.slots?.[k] ?? k;
      const size = Math.max(val.length, placeholder.length, 6);
      return `<input type="text" class="slot" data-key="${k}" value="${escapeHtml(val)}" placeholder="${escapeHtml(placeholder)}" size="${size}" />`;
    }).join('');
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

customElements.define('ccpw-prompt-card', CCPWPromptCard);

// expose helper for ccpw-prompt-list
declare global { interface HTMLElement { prompt?: Prompt; } }